import React, { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { PackageCard } from "@/components/PackageCard";
import { Reveal } from "@/components/Reveal";
import { api } from "@/lib/apiClient";
import { waLink } from "@/data/site";
import { MessageCircle } from "lucide-react";
import Seo from "@/components/Seo";

// Generic editorial listing page for a travel category / tour type.
export default function TourCategory({ title, seoTitle, description, intro, params = {}, heroImage }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search] = useSearchParams();

  useEffect(() => {
    setLoading(true);
    const region = search.get("region");
    api.get("/itineraries", { params: { published: true, ...params, ...(region ? { region } : {}) } })
      .then((r) => setItems(r.data))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(params), search]);

  return (
    <div className="bg-[#FFF7EC] min-h-screen">
      <Seo title={seoTitle || title} description={description} />
      {/* Editorial hero */}
      <div className="relative h-[52vh] min-h-[380px]">
        <img src={heroImage} alt={title} className="h-full w-full object-cover" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(42,24,8,0.5) 0%, rgba(42,24,8,0.2) 45%, rgba(255,247,236,1) 100%)" }} />
        <div className="absolute inset-0 flex items-end">
          <div className="max-w-7xl mx-auto w-full px-4 sm:px-8 lg:px-12 pb-12">
            <Reveal>
              <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-medium tracking-tight text-white leading-[1.02]" style={{ textShadow: "0 2px 30px rgba(0,0,0,0.4)" }}>{title}</h1>
              <p className="mt-4 max-w-2xl text-lg text-[#F9EFE1]/90">{intro}</p>
            </Reveal>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-16">
        {loading ? <p className="text-[#8A6B47]">Loading journeys…</p> : items.length === 0 ? (
          <div className="text-center py-16" data-testid="category-empty">
            <p className="font-display text-3xl text-[#2A1808]">More journeys coming here soon</p>
            <p className="mt-3 text-[#553204]">Tell us what you have in mind and we'll craft it from scratch.</p>
            <a href={waLink()} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 mt-6 rounded-full btn-orange px-7 py-3 text-sm"><MessageCircle size={16} /> Plan on WhatsApp</a>
            <Link to="/packages" className="ml-3 inline-block mt-6 text-[#F49E1A] font-semibold">or browse all journeys →</Link>
          </div>
        ) : (
          <>
            <p className="text-sm text-[#8A6B47] mb-6" data-testid="category-count">{items.length} journeys</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
              {items.map((it) => <Reveal key={it.slug}><PackageCard item={it} /></Reveal>)}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
